// The interviewer's own debrief view (D20, DC-174, V-12): for each candidate of the cycle on which the viewer holds a
// scorecard, the viewer's own Scorecard as submitted; when the viewer revised it in the room, DiffRevise in its view
// form (Original · Revised per section, the recommendation both ways and the Rationale); then the viewer's Final
// alignment. Nobody else's card is read (V1 `debrief.open` carries only `my_scorecard`; `scorecard.open` the author's).
// The original revision's ratings are read from `original_ratings` when the server sends them (S09); until then a
// revised card shows its current revision only. B14: every recommendation word and tone is C5's (`recommendationWord`),
// so Strong Hire reads green and Consider Other Role orange; a skill is named by its intake title, never a number.

import { debriefListMine, debriefOpen, scorecardOpen, type MemberOut } from "@/api/tools";
import { useRead } from "@/api/useTool";
import {
  Card, DEBRIEF_STATUS, DiffRevise, EmptyState, McRoot, NAV_ITEM_LABELS, PageHeader, Pill, RECO_PILL_TONE, SCORECARD_STATUS,
  SectionSummary, Stack, TextLink, recommendationWord, type DiffReviseRow,
} from "@/mc";
import { stageOf } from "./Debriefs";
import { SCORE_LABELS } from "./labels";
import { dimensionLabel, dimensionsOf, dimKey, entriesOf, playbookOf, skillTitlesOf, type Dimension, type Entry } from "./playbook";

const records = (value: unknown): Record<string, unknown>[] | undefined =>
  Array.isArray(value) ? (value.filter((row) => row !== null && typeof row === "object") as Record<string, unknown>[]) : undefined;

/** DiffRevise's rows: each section's original beside its revised value; a skill by its title. */
export function diffRows(
  dims: readonly Dimension[], original: Record<string, Entry>, revised: Record<string, Entry>, skillTitles: Readonly<Record<string, string>> = {},
): DiffReviseRow[] {
  return dims.map((d) => ({
    key: dimKey(d), label: dimensionLabel(d, skillTitles) ?? "—", original: original[dimKey(d)]?.rating ?? null, revised: revised[dimKey(d)]?.rating ?? null,
  }));
}

/** The viewer's own alignment on the member (its stored value after the reveal); an unknown value is null. */
export function finalAlignmentOf(member: Pick<MemberOut, "alignments">): string | null {
  const mine = member.alignments.find((row) => row["mine"] === true);
  const value = mine?.["value"];
  return typeof value === "string" && recommendationWord(value) ? value : null;
}

/** A stored recommendation's string value, or undefined. */
const recoValue = (value: unknown): string | undefined => (typeof value === "string" && recommendationWord(value) ? value : undefined);

/** The final alignment's pill: C5's word in C5's tone (Strong Hire green, Consider Other Role orange, No Hire red). */
export function AlignmentPill({ value }: { value: string }) {
  const word = recommendationWord(value);
  if (!word) return null;
  return <Pill tone={RECO_PILL_TONE[word.tone]}>{word.label}</Pill>;
}

function MyCard({ member, index }: { member: MemberOut; index: number }) {
  const scorecardId = String(member.my_scorecard?.["scorecard_id"] ?? "");
  const packetId = member.my_scorecard?.["packet_id"];
  const open = useRead(["scorecard", "id", scorecardId], () => scorecardOpen({ scorecard_id: scorecardId }), { enabled: scorecardId !== "" });
  const aligned = finalAlignmentOf(member);
  const card = open.data;
  const raw = (card ?? {}) as Record<string, unknown>;
  const dims = card ? dimensionsOf(card) : [];
  const titles = skillTitlesOf(card ? playbookOf(card).guide : null);
  const current = entriesOf(card?.ratings ?? records(card?.revision?.["ratings"]));
  const originalRows = records(raw["original_ratings"]);
  const revised = Boolean(card && card.current_revision_no > 1 && originalRows);
  const rationale = card?.revision?.["revision_reason"];
  return (
    <Card
      title={member.candidate}
      
      loading={open.isLoading ? true : undefined}
      pills={member.my_scorecard?.["submitted"] === true ? <Pill tone="ok">{SCORECARD_STATUS.submitted}</Pill> : undefined}
      actions={typeof packetId === "string" ? <TextLink href={`#interviews/${packetId}/${member.application_id}`}>{SCORE_LABELS.scorecard}</TextLink> : undefined}
    >
      <Stack gap={12}>
        {revised ? (
          <DiffRevise
            mode="view"
            rows={diffRows(dims, entriesOf(originalRows), current, titles)}
            originalReco={recoValue(raw["original_recommendation"])}
            revisedReco={recoValue(card?.revision?.["recommendation"]) ?? null}
            offered={card ? playbookOf(card).level.offered : undefined}
            rationale={typeof rationale === "string" ? rationale : undefined}
          />
        ) : (
          <SectionSummary sections={dims.map((d) => ({ label: dimensionLabel(d, titles) ?? "—", rating: current[dimKey(d)]?.rating ?? null, evidence: current[dimKey(d)]?.evidence }))} />
        )}
        {aligned ? <p><b>{SCORE_LABELS.finalAlignment}</b> <AlignmentPill value={aligned} /></p> : null}
      </Stack>
    </Card>
  );
}

function MyDebriefBody({ cycleId }: { cycleId: string }) {
  const list = useRead(["debriefs", "mine"], () => debriefListMine({}));
  const row = list.data?.items.find((item) => item.cycle_id === cycleId);
  const room = useRead(["debriefs", cycleId], () => debriefOpen({ requisition_id: row?.requisition_id ?? "", cycle_id: cycleId }), { enabled: Boolean(row) });
  if (list.isLoading || room.isLoading) return <Card loading />;
  if (!row || !room.data) return <EmptyState revoked>{NAV_ITEM_LABELS.debriefs}</EmptyState>;
  const stage = stageOf(room.data.status);
  const mine = room.data.members.filter((member) => member.my_scorecard);
  return (
    <Stack gap={16}>
      <PageHeader
        title={NAV_ITEM_LABELS.debriefs}
        pills={stage ? <Pill tone="info">{DEBRIEF_STATUS[stage]}</Pill> : undefined}
        meta={[room.data.requisition_title]}
      />
      {mine.length === 0 ? <EmptyState inline>{SCORE_LABELS.scorecard}</EmptyState> : mine.map((member, i) => <MyCard key={member.application_id} member={member} index={i} />)}
    </Stack>
  );
}

export function MyDebrief({ cycleId }: { cycleId: string }) {
  return <McRoot><MyDebriefBody cycleId={cycleId} /></McRoot>;
}
